import { useEffect, useRef, useState } from "react";
import { useRH } from "../store/rh";
import { useSettings } from "../store/settings";
import { PERSONAS } from "../data/org";
import { profileOf } from "../data/profiles";
import { initials, monogramColor } from "../lib/monogram";
import { chatWithAgentAI } from "../lib/claude";
import { speak, TTS_VOICES, type TTSVoice, type SpeakController } from "../lib/tts";

interface Msg { role: "user" | "assistant"; content: string; at: number; }

export function AgentChat({ initialAgentId, onClose }:
  { initialAgentId?: string; onClose: () => void }) {
  const settings = useSettings();
  const employees = useRH(s => s.employees);
  const hired = PERSONAS.filter(p => employees[p.id]?.status === "hired");

  const [agentId, setAgentId] = useState(initialAgentId ?? hired[0]?.id ?? "");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<Record<string, Msg[]>>({});
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [voice, setVoice] = useState<TTSVoice>(settings.openaiKey ? "onyx" : "browser-default");
  const [autoSpeak, setAutoSpeak] = useState(true);
  const recogRef = useRef<any>(null);
  const ctrlRef = useRef<SpeakController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const agent = PERSONAS.find(p => p.id === agentId);
  const msgs = history[agentId] ?? [];

  useEffect(() => {
    scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight);
  }, [msgs.length, agentId]);

  const stopSpeaking = () => {
    ctrlRef.current?.stop();
    ctrlRef.current = null;
    setSpeaking(false);
  };

  const send = async (text?: string) => {
    const content = (text ?? draft).trim();
    if (!content || !agent) return;
    if (!settings.anthropicKey) { alert("Configure chave Anthropic em ⚙️."); return; }
    setDraft("");
    stopSpeaking();

    const userMsg: Msg = { role: "user", content, at: Date.now() };
    setHistory(h => ({ ...h, [agentId]: [...(h[agentId] ?? []), userMsg] }));
    setBusy(true);

    try {
      const prof = profileOf(agent.id, agent.tier);
      const customPrompt = employees[agent.id]?.systemPromptOverride;
      // monta history pro Claude (sem timestamps)
      const apiHistory = (history[agentId] ?? []).map(m => ({ role: m.role, content: m.content }));
      const reply = await chatWithAgentAI({
        apiKey: settings.anthropicKey, model: settings.model,
        agentName: agent.name, agentVoice: prof.voice,
        agentSkills: prof.skills, agentExpectations: prof.expectations,
        customSystemPrompt: customPrompt,
        history: apiHistory, userMessage: content
      });
      const asst: Msg = { role: "assistant", content: reply, at: Date.now() };
      setHistory(h => ({ ...h, [agentId]: [...(h[agentId] ?? []), asst] }));
      // TTS automático
      if (autoSpeak && reply) {
        setSpeaking(true);
        try {
          const ctrl = await speak({
            text: reply, voice,
            apiKey: voice === "browser-default" ? undefined : settings.openaiKey
          });
          ctrlRef.current = ctrl;
          await ctrl.done;
          setSpeaking(false);
        } catch {
          setSpeaking(false);
        }
      }
    } catch (e) {
      const err: Msg = { role: "assistant", content: "✗ " + (e as Error).message, at: Date.now() };
      setHistory(h => ({ ...h, [agentId]: [...(h[agentId] ?? []), err] }));
    } finally { setBusy(false); }
  };

  const startListening = () => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) { alert("Seu navegador não suporta Speech Recognition. Use Chrome/Edge."); return; }
    const r = new SR();
    r.lang = "pt-BR";
    r.continuous = false;
    r.interimResults = true;
    r.maxAlternatives = 1;
    let finalText = "";
    r.onresult = (ev: any) => {
      finalText = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        finalText += ev.results[i][0].transcript;
      }
      setDraft(finalText);
    };
    r.onerror = (ev: any) => {
      console.error("speech error", ev);
      setListening(false);
    };
    r.onend = () => {
      setListening(false);
      if (finalText.trim()) {
        setTimeout(() => send(finalText.trim()), 200);
      }
    };
    recogRef.current = r;
    r.start();
    setListening(true);
  };

  const stopListening = () => {
    recogRef.current?.stop();
    setListening(false);
  };

  const speakAgain = async (text: string) => {
    stopSpeaking();
    setSpeaking(true);
    try {
      const ctrl = await speak({
        text, voice,
        apiKey: voice === "browser-default" ? undefined : settings.openaiKey
      });
      ctrlRef.current = ctrl;
      await ctrl.done;
    } finally { setSpeaking(false); }
  };

  useEffect(() => () => { stopSpeaking(); recogRef.current?.stop?.(); }, []);

  return (
    <div className="fixed inset-0 z-50 bg-ink/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-bone border-2 border-ink w-full max-w-4xl h-[90vh] flex flex-col shadow-[6px_6px_0_0_#121214]"
           onClick={(e) => e.stopPropagation()}>
        <header className="bg-ink text-bone px-5 py-3 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-3">
            {agent && (
              <div className={`w-10 h-10 flex items-center justify-center font-display font-black overflow-hidden ${speaking ? "ring-2 ring-gold animate-pulse" : ""}`}
                   style={{ background: monogramColor(agent.id), color: "#F2EEE6" }}>
                {employees[agent.id]?.avatarUrl
                  ? <img src={employees[agent.id]!.avatarUrl} alt="" className="w-full h-full object-cover" />
                  : initials(agent.name)}
              </div>
            )}
            <div>
              <div className="font-mono text-xs text-gold">CHAT-LIVE</div>
              <div className="font-display font-black uppercase text-xl leading-none">
                {agent?.name ?? "—"}
              </div>
              <div className="font-crimson italic text-bone/70 text-xs">{agent?.bio.slice(0, 80)}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select value={agentId} onChange={(e) => { stopSpeaking(); setAgentId(e.target.value); }}
              className="bg-bone text-ink px-2 py-1 font-mono text-[12px] border border-bone">
              {hired.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <button onClick={onClose} className="btn bg-red text-bone border-red">× fechar</button>
          </div>
        </header>

        {/* CONVERSA */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-bone">
          {msgs.length === 0 && (
            <div className="font-crimson italic text-paper text-center mt-12">
              Comece a conversa com {agent?.name ?? "o agente"}. Digite ou clique no microfone 🎙️.
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] border-2 p-3
                ${m.role === "user" ? "bg-ink text-bone border-ink" : "bg-bone border-ink shadow-[3px_3px_0_0_#121214]"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="tag opacity-60">
                    {m.role === "user" ? "MARCIO" : agent?.name.toUpperCase() ?? "AGENTE"}
                  </span>
                  <span className="tag opacity-50">
                    {new Date(m.at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                <p className="font-mono text-[13px] whitespace-pre-wrap leading-relaxed">{m.content}</p>
                {m.role === "assistant" && (
                  <button onClick={() => speakAgain(m.content)}
                    className="tag underline mt-1 opacity-60 hover:opacity-100">🔊 ouvir</button>
                )}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="border-2 border-ink bg-bone p-3">
                <span className="font-mono text-[12px] animate-pulse">{agent?.name} pensando…</span>
              </div>
            </div>
          )}
        </div>

        {/* INPUT */}
        <div className="border-t-2 border-ink p-3 bg-bone">
          <div className="flex gap-2 mb-2">
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={2}
              placeholder={listening ? "🎙️ falando…" : `Pergunta pro ${agent?.name ?? "agente"}…`}
              className="flex-1 border-2 border-ink bg-bone px-2 py-1 font-mono text-[13px] resize-none" />
            <div className="flex flex-col gap-1">
              {!listening ? (
                <button onClick={startListening}
                  className="btn bg-ink text-bone border-ink"
                  title="Falar por voz">🎙️ voz</button>
              ) : (
                <button onClick={stopListening}
                  className="btn bg-red text-bone border-red animate-pulse"
                  title="Parar gravação">⏹ parar</button>
              )}
              <button onClick={() => send()} disabled={busy || !draft.trim()}
                className="btn bg-red text-bone border-red disabled:opacity-50">▶ enviar</button>
            </div>
          </div>
          <details className="text-[10px]">
            <summary className="cursor-pointer tag text-graydk">⚙ voz / TTS</summary>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <select value={voice} onChange={(e) => setVoice(e.target.value as TTSVoice)}
                className="border border-ink bg-bone px-1 py-0.5 font-mono text-[11px]">
                {TTS_VOICES.map(v => (
                  <option key={v.id} value={v.id}
                    disabled={v.id !== "browser-default" && !settings.openaiKey}>
                    {v.label}
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-1 font-mono text-[11px]">
                <input type="checkbox" checked={autoSpeak}
                  onChange={(e) => setAutoSpeak(e.target.checked)} />
                falar resposta automaticamente
              </label>
              {speaking && (
                <button onClick={stopSpeaking} className="tag text-red underline">⏹ parar voz</button>
              )}
              <span className="font-mono text-graydk">Enter envia · Shift+Enter quebra linha</span>
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}
